import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

interface PackageDetailsFormProps {
  packageId: string;
  selectedPackage?: {
    id: string;
    destination: string;
    flight: { route: string; carrier: string; logo: string };
    stay: { name: string; nights: number; image: string };
    totalPrice: number;
    savings: number;
  };
  onComplete: (data: {
    startDate: string;
    endDate: string;
    travelers: number;
    rooms: number;
    specialRequests?: string;
  }) => void;
  initialData: {
    startDate: string;
    endDate: string;
    travelers: number;
    rooms: number;
    specialRequests?: string;
  };
}

export default function PackageDetailsForm({ packageId, selectedPackage, onComplete, initialData }: PackageDetailsFormProps) {
  const [formData, setFormData] = useState(initialData);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onComplete(formData);
  };

  const isFormValid = formData.startDate && formData.endDate && formData.travelers > 0 && formData.rooms > 0;

  return (
    <div>
      <h2 className="text-2xl font-semibold mb-6 text-foreground">Travel Details</h2>
      {selectedPackage ? (
        <div className="bg-accent/5 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-lg text-foreground mb-3">{selectedPackage.destination}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">Flight</p>
              <div className="flex items-center gap-2">
                <img src={selectedPackage.flight.logo} alt={selectedPackage.flight.carrier} className="w-6 h-6" />
                <p className="text-foreground">{selectedPackage.flight.route}</p>
              </div>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Accommodation</p>
              <p className="text-foreground">{selectedPackage.stay.name} • {selectedPackage.stay.nights} nights</p>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-border">
            <div className="flex justify-between items-center">
              <p className="text-lg font-semibold text-foreground">Total Package Price</p>
              <p className="text-2xl font-bold text-primary">AED {selectedPackage.totalPrice.toLocaleString()}</p>
            </div>
          </div>
        </div>
      ) : (
        <p className="text-muted-foreground mb-8">
          Please provide your travel details to customize your package booking.
        </p>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="startDate" className="text-sm font-medium text-foreground">
              Departure Date
            </Label>
            <input
              id="startDate"
              type="date"
              value={formData.startDate}
              onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-full mt-1 px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              min={new Date().toISOString().split('T')[0]}
              required
            />
          </div>

          <div>
            <Label htmlFor="endDate" className="text-sm font-medium text-foreground">
              Return Date
            </Label>
            <input
              id="endDate"
              type="date"
              value={formData.endDate}
              onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-full mt-1 px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              min={formData.startDate || new Date().toISOString().split('T')[0]}
              required
            />
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="travelers" className="text-sm font-medium text-foreground">
              Number of Travelers
            </Label>
            <select
              id="travelers"
              value={formData.travelers}
              onChange={(e) => setFormData(prev => ({ ...prev, travelers: Number(e.target.value) }))}
              className="w-full mt-1 px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value={1}>1 Adult</option>
              <option value={2}>2 Adults</option>
              <option value={3}>3 Adults</option>
              <option value={4}>4 Adults</option>
              <option value={5}>5 Adults</option>
              <option value={6}>6 Adults</option>
            </select>
          </div>

          <div>
            <Label htmlFor="rooms" className="text-sm font-medium text-foreground">
              Number of Rooms
            </Label>
            <select
              id="rooms"
              value={formData.rooms}
              onChange={(e) => setFormData(prev => ({ ...prev, rooms: Number(e.target.value) }))}
              className="w-full mt-1 px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            >
              <option value={1}>1 Room</option>
              <option value={2}>2 Rooms</option>
              <option value={3}>3 Rooms</option>
              <option value={4}>4 Rooms</option>
            </select>
          </div>
        </div>

        <div>
          <Label htmlFor="specialRequests" className="text-sm font-medium text-foreground">
            Special Requests (Optional)
          </Label>
          <textarea
            id="specialRequests"
            value={formData.specialRequests || ""}
            onChange={(e) => setFormData(prev => ({ ...prev, specialRequests: e.target.value }))}
            className="w-full mt-1 px-4 py-3 border border-border rounded-lg bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            rows={4}
            placeholder="Dietary requirements, accessibility needs, special occasions, etc."
          />
        </div>

        <div className="flex justify-end">
          <Button 
            type="submit"
            disabled={!isFormValid}
            className="px-8 py-3"
          >
            Continue to Payment Options
          </Button>
        </div>
      </form>
    </div>
  );
}